import { useState, useEffect } from "react";
import { Form, Schema } from "rsuite";
import { StringType } from "schema-typed";
import { useHistory, useParams } from "react-router-dom";
import { Button } from "../../components/button.component";
import { TextInput } from "../../components/textInput.component";
import { SpinLoader } from "../../components/spinLoader.component";
import { ApiDataExchangeConfig } from "../../core/apis/dataExchangeConfig";
import { AppRouteUi } from "../../config/appRoute";
import { showSuccessToast, showErrorToast } from "../../components/toast.component";
import { useAppDispatch } from "../../core/redux/ReduxStore";
import { ActionsHHAConfigTable } from "../../core/redux/slices/sliceHHAConfigTable";
import { DoesObjectHasAnyInvalidValue } from "../../utils/formUtils";

interface FormData {
  companyId: string;
  appName: string;
  appKey: string;
  appSecret: string;
  clientId: string;
  clientSecret: string;
  tokenUrl: string;
  dataUrl: string;
  patientDataUrl: string;
  scope: string;
  grantType: string;
}

const formModel = Schema.Model({
  appName: StringType()
    .isRequired('App Name is required')
    .maxLength(200, 'App Name cannot exceed 200 characters'),
  appKey: StringType()
    .isRequired('App Key is required')
    .maxLength(200, 'App Key cannot exceed 200 characters'),
  appSecret: StringType()
    .isRequired('App Secret is required')
    .maxLength(200, 'App Secret cannot exceed 200 characters'),
  clientId: StringType()
    .isRequired('Client ID is required')
    .maxLength(200, 'Client ID cannot exceed 200 characters'),
  clientSecret: StringType()
    .isRequired('Client Secret is required')
    .maxLength(200, 'Client Secret cannot exceed 200 characters'),
  tokenUrl: StringType()
    .isRequired('Token URL is required')
    .pattern(/^https?:\/\/.+/, 'Token URL must be a valid URL starting with http:// or https://'),
  dataUrl: StringType()
    .isRequired('Data URL is required')
    .pattern(/^https?:\/\/.+/, 'Data URL must be a valid URL starting with http:// or https://'),
  patientDataUrl: StringType()
    .isRequired('Patient Data URL is required')
    .pattern(/^https?:\/\/.+/, 'Patient Data URL must be a valid URL starting with http:// or https://'),
  scope: StringType()
    .isRequired('Scope is required'),
  grantType: StringType()
    .isRequired('Grant Type is required')
});

export const HHAExchangeConfigEdit = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);
  const [formError, setFormError] = useState<any>({});
  const [formData, setFormData] = useState<FormData>({
    companyId: "",
    appName: "",
    appKey: "",
    appSecret: "",
    clientId: "",
    clientSecret: "",
    tokenUrl: "",
    dataUrl: "",
    patientDataUrl: "",
    scope: "",
    grantType: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ApiDataExchangeConfig.GetByCompanyId(id);
        if (response.isSuccess && response.data) {
          setFormData({
            companyId: response.data.companyId,
            appName: response.data.appName,
            appKey: response.data.appKey,
            appSecret: response.data.appSecret,
            clientId: response.data.clientId,
            clientSecret: response.data.clientSecret,
            tokenUrl: response.data.tokenUrl,
            dataUrl: response.data.dataUrl,
            patientDataUrl: response.data.patientDataUrl,
            scope: response.data.scope,
            grantType: response.data.grantType,
          });
        } else {
          showErrorToast({ isSuccess: false, message: "Failed to fetch HHA configuration" });
          history.push(AppRouteUi.HHAConfig.Root());
        }
      } catch (error: any) {
        const errorMessage = error.message || "Failed to fetch HHA configuration";
        showErrorToast({ isSuccess: false, message: errorMessage });
        history.push(AppRouteUi.HHAConfig.Root());
      } finally {
        setIsValidating(false);
      }
    };
    fetchData();
  }, [id, history]);

  useEffect(() => {
    const isEmpty = Object.values({ ...formData }).some(DoesObjectHasAnyInvalidValue);
    const errorCount = Object.keys(formError).length;
    setIsDisabled(isEmpty || !!errorCount);
  }, [formData, formError]);

  const handleSubmit = async () => {
    if (isDisabled) return;
    
    setIsLoading(true);
    try {
      const response = await ApiDataExchangeConfig.Update(id, formData);
      if (response.isSuccess) {
        showSuccessToast("HHA Configuration updated successfully");
        dispatch(ActionsHHAConfigTable.setRefetch(true));
        history.push(AppRouteUi.HHAConfig.Root());
      } else {
        showErrorToast({ isSuccess: false, message: response.message || "Failed to update HHA configuration" });
      }
    } catch (error: any) {
      const errorMessage = error.message || "Failed to update HHA configuration";
      showErrorToast({ isSuccess: false, message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (formValue: any) => {
    setFormData(formValue);
  };

  return (
    <div className="col-md-10 col-12 border-end px-4 pb-3 add-page-ctl">
      {isValidating ? (
        <SpinLoader />
      ) : (
        <Form 
          fluid 
          onChange={handleChange} 
          formValue={formData} 
          onSubmit={handleSubmit}
          model={formModel}
          onCheck={setFormError}
          className="form-validation"
        >
          <div className="row">
            <TextInput name="appName" required>
              App Name
            </TextInput>
            <TextInput name="appKey" required>
              App Key
            </TextInput>
          </div>

          <div className="row">
            <TextInput name="appSecret" required>
              App Secret
            </TextInput>
            <TextInput name="clientId" required>
              Client ID
            </TextInput>
          </div>

          <div className="row">
            <TextInput name="clientSecret" required>
              Client Secret
            </TextInput>
            <TextInput name="tokenUrl" required>
              Token URL
            </TextInput>
          </div>

          <div className="row">
            <TextInput name="dataUrl" required>
              Data URL
            </TextInput>
            <TextInput name="patientDataUrl" required>
              Patient Data URL
            </TextInput>
          </div>

          <div className="row">
            <TextInput name="scope" required>
              Scope
            </TextInput>
            <TextInput name="grantType" required>
              Grant Type
            </TextInput>
          </div>

          <div className="d-flex justify-content-center mt-4">
            <Button
              className="px-5 me-3"
              name="Cancel"
              onClick={() => history.push(AppRouteUi.HHAConfig.Root())}
            />
            <Button
              className="px-5 btn-green"
              type="submit"
              loading={isLoading}
              disabled={isDisabled}
              name="Update"
            />
          </div>
        </Form>
      )}
    </div>
  );
};